import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  LOCALES,
  LOCALE_LABELS,
  PRODUCT_KINDS,
  formatPrice,
  groszToZloty,
  productInputSchema,
  zlotyToGrosz,
  type Locale,
  type Product,
  type ProductInput,
  type ProductKind,
  type ProductPatch,
} from '@diana/shared'
import { api } from '../../lib/api.js'
import { Column, Main, TopTitle } from '../../classroom/Shell.js'
import { Button, ErrorLine, Skeleton, Status, Tabs } from '../../classroom/ui.js'
import {
  Check,
  CoverLine,
  Field,
  Grip,
  More,
  asLocale,
  byOrder,
  errorMessage,
  orderChanges,
  slugify,
  useDragOrder,
  useInvalidate,
  useListCache,
  useReorder,
} from './editor.js'

/**
 * /produkty from Diana's side (Studio-Products.png): courses and the bundle,
 * then the free materials, one language at a time.
 *
 * „Edytuj” (or „+ Nowy produkt”) swaps the list for the product's form; the
 * URL says which (?edytuj=…), so the browser's back button returns to the
 * list. There is no checkout yet, so every button leads to a link she sets —
 * her shop, Telegram, a form.
 */
const NEW = 'nowy'

type Go = (next: { locale: Locale; edit?: string }) => void

export function ProductsAdminPage() {
  const [params, setParams] = useSearchParams()
  const locale = asLocale(params.get('jezyk'))
  const edit = params.get('edytuj')
  const { data, isPending, isError } = useQuery({ queryKey: ['studio', 'products'], queryFn: api.studio.products })

  const go: Go = (next) => {
    const p = new URLSearchParams()
    if (next.locale !== 'pl') p.set('jezyk', next.locale)
    if (next.edit) p.set('edytuj', next.edit)
    setParams(p)
  }

  if (isPending || isError || !data) {
    return (
      <Column density="studio" top={<TopTitle studio>Produkty</TopTitle>}>
        <Main density="studio">{isPending ? <Skeleton lines={6} /> : <ErrorLine>Nie udało się wczytać produktów. Odśwież stronę.</ErrorLine>}</Main>
      </Column>
    )
  }

  const items = byOrder(data.filter((p) => p.locale === locale))
  const original = edit && edit !== NEW ? data.find((p) => p.id === edit) : undefined
  if (edit === NEW || original) {
    return <ProductEditor key={edit} locale={original?.locale ?? locale} original={original} order={items.length} go={go} />
  }
  return <ProductList locale={locale} items={items} go={go} />
}

/* ── the list ──────────────────────────────────────────────────────────── */

const KIND_SHORT: Record<ProductKind, string> = { course: 'Kurs', bundle: 'Pakiet', free: 'Materiał' }

function ProductList({ locale, items, go }: { locale: Locale; items: Product[]; go: Go }) {
  const paid = items.filter((p) => p.kind !== 'free')
  const free = items.filter((p) => p.kind === 'free')
  const reorder = useReorder('products', (id, order) => api.studio.updateProduct(id, { order }))

  /**
   * The two sections share one order (the site splits them by kind), so a
   * section's new order is written back into the places its rows held.
   */
  function commit(sectionIds: string[]) {
    const inSection = new Set(sectionIds)
    let k = 0
    const next = items.map((p) => (inSection.has(p.id) ? sectionIds[k++]! : p.id))
    const changes = orderChanges(next, items)
    if (changes.length > 0) reorder.mutate(changes)
  }

  return (
    <Column
      density="studio"
      topOnPhone
      top={
        <>
          <TopTitle studio>Produkty</TopTitle>
          <Button className="ml-auto min-h-10 px-5 text-class-gloss" onClick={() => go({ locale, edit: NEW })}>
            + Nowy produkt
          </Button>
        </>
      }
    >
      <Main density="studio">
        <section className="flex flex-col">
          <div className="flex flex-wrap items-end gap-x-9 gap-y-2 border-b border-class-line">
            <h1 className="cl-heading m-0 mb-2.5">Kursy i pakiety</h1>
            <Tabs label="Język strony" value={locale} onChange={(l) => go({ locale: l })} items={LOCALES.map((l) => ({ value: l, label: LOCALE_LABELS[l] }))} />
          </div>
          <p className="m-0 mt-4 text-class-gloss text-class-ink-soft">
            {items.length === 0 && locale !== 'pl'
              ? 'Dopóki nie dodasz tu produktów, strona w tym języku pokazuje polskie.'
              : 'Strona „Produkty”. Zmiany są widoczne od razu. Koszyka jeszcze nie ma — „Kup kurs” prowadzi pod link przycisku.'}
          </p>
          {paid.length > 0 ? (
            <ProductTable items={paid} head locale={locale} go={go} onReorder={commit} busy={reorder.isPending} />
          ) : (
            items.length > 0 && <p className="m-0 mt-5 text-class-ink-soft">W tym języku nie ma jeszcze kursów.</p>
          )}
          {reorder.isError && (
            <div className="mt-3">
              <ErrorLine>Nie udało się zapisać kolejności. Spróbuj jeszcze raz.</ErrorLine>
            </div>
          )}
        </section>

        {free.length > 0 && (
          <section className="flex flex-col gap-4">
            <h2 className="cl-subheading m-0">Za darmo</h2>
            <ProductTable items={free} locale={locale} go={go} onReorder={commit} busy={reorder.isPending} />
          </section>
        )}
      </Main>
    </Column>
  )
}

/** Grip · Produkt · Rodzaj · Cena · Na stronie · Edytuj — the mockup's table, as a divider list. */
const COLS = 'grid grid-cols-[24px_minmax(0,1fr)_auto_auto] items-center gap-x-3 desk:grid-cols-[24px_minmax(0,1fr)_120px_130px_150px_70px] desk:gap-x-0'

function ProductTable({
  items,
  head = false,
  locale,
  go,
  onReorder,
  busy,
}: {
  items: Product[]
  head?: boolean
  locale: Locale
  go: Go
  onReorder: (ids: string[]) => void
  busy: boolean
}) {
  const drag = useDragOrder(
    items.map((p) => p.id),
    onReorder,
    busy,
  )

  return (
    <div role="table" aria-label={head ? 'Kursy i pakiety' : 'Za darmo'} className={head ? 'mt-5' : ''}>
      {head && (
        <div role="row" className={`${COLS} pb-3 text-class-label font-semibold text-class-ink-faint`}>
          <span role="columnheader">
            <span className="sr-only">Kolejność</span>
          </span>
          <span role="columnheader">Produkt</span>
          <span role="columnheader" className="hidden px-4 desk:block">
            Rodzaj
          </span>
          <span role="columnheader" className="text-right desk:px-4">
            Cena
          </span>
          <span role="columnheader" className="hidden px-4 desk:block">
            Na stronie
          </span>
          <span role="columnheader">
            <span className="sr-only">Edycja</span>
          </span>
        </div>
      )}
      {drag.order.map((pid) => {
        const p = items.find((x) => x.id === pid)!
        const edit = () => go({ locale, edit: p.id })
        const status = <Status tone={p.active ? 'ok' : 'neutral'}>{p.active ? 'Widoczny' : 'Ukryty'}</Status>
        return (
          <div
            key={p.id}
            ref={drag.row(p.id)}
            role="row"
            className={`${COLS} min-h-16 border-t border-class-line py-2 ${drag.dragging === p.id ? 'bg-class-field-soft' : ''}`}
          >
            <span role="cell" className="-ml-1.5">
              <Grip {...drag.grip(p.id, `Przesuń „${p.title}”`)} />
            </span>
            <span role="cell" className="min-w-0 leading-snug">
              <button type="button" onClick={edit} className="block w-full cursor-pointer border-0 bg-transparent p-0 text-left font-class-text text-class-body text-class-ink">
                <span className="font-semibold">{p.title}</span>
                {p.featured && <span className="text-class-label font-semibold text-class-ink-faint"> · {p.badge || 'Wyróżniony'}</span>}
                {productMeta(p) && <span className="block text-class-gloss text-class-ink-faint">{productMeta(p)}</span>}
              </button>
              <span className="mt-1 block desk:hidden">{status}</span>
            </span>
            <span role="cell" className="hidden px-4 text-class-gloss text-class-ink-soft desk:block">
              {KIND_SHORT[p.kind]}
            </span>
            <span role="cell" className="text-right text-class-gloss whitespace-nowrap desk:px-4">
              {p.kind === 'free' || p.priceGrosz === 0 ? (
                <span className="font-semibold">bezpłatnie</span>
              ) : (
                <>
                  <span className="font-semibold">{formatPrice(p.priceGrosz)}</span>
                  {p.compareAtGrosz > p.priceGrosz && <s className="ml-1 text-class-label text-class-ink-faint">{formatPrice(p.compareAtGrosz)}</s>}
                </>
              )}
            </span>
            <span role="cell" className="hidden px-4 desk:block">
              {status}
            </span>
            <span role="cell" className="text-right">
              <button type="button" onClick={edit} className="cl-link text-class-gloss">
                Edytuj
              </button>
            </span>
          </div>
        )
      })}
    </div>
  )
}

/** „Od zera do pierwszych rozmów · 3 miesiące” — the line under a product's name. */
function productMeta(p: Product): string {
  return [p.subtitle || p.level, p.duration].filter(Boolean).join(' · ')
}

/* ── the form ──────────────────────────────────────────────────────────── */

const KIND_OPTION: Record<ProductKind, string> = {
  course: 'Kurs',
  bundle: 'Pakiet kursów — pokazuje cenę regularną i oszczędność',
  free: 'Za darmo — w sekcji „Za darmo”, bez ceny',
}

function ProductEditor({ locale, original, order, go }: { locale: Locale; original?: Product; order: number; go: Go }) {
  const invalidate = useInvalidate('products')
  const cache = useListCache<Product>('products')
  const baseline = original ? draftFromProduct(original) : emptyDraft(locale, order)
  const [d, setD] = useState<Draft>(baseline)
  const [note, setNote] = useState<string | null>(null)
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setNote(null)
    setD((prev) => ({ ...prev, [key]: value }))
  }
  const dirty = JSON.stringify(d) !== JSON.stringify(baseline)

  function back() {
    if (dirty && !window.confirm('Masz niezapisane zmiany. Porzucić je?')) return
    go({ locale })
  }

  const save = useMutation({
    mutationFn: async (input: ProductInput) => {
      if (!original) return api.studio.createProduct(input)
      const patch = diffProduct(original, input)
      if (Object.keys(patch).length === 0) return original
      return api.studio.updateProduct(original.id, patch)
    },
    onSuccess: (row) => {
      cache.put(row)
      invalidate()
      go({ locale: row.locale })
    },
    onError: (err) => setNote(errorMessage(err)),
  })

  const remove = useMutation({
    mutationFn: () => api.studio.deleteProduct(original!.id),
    onSuccess: () => {
      cache.drop(original!.id)
      invalidate()
      go({ locale })
    },
    onError: () => setNote('Nie udało się usunąć. Spróbuj jeszcze raz.'),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const result = draftToInput(d)
    if ('error' in result) {
      setNote(result.error)
      return
    }
    setNote(null)
    save.mutate(result.input)
  }

  const id = (name: string) => `product-${name}`
  const isFree = d.kind === 'free'

  return (
    <Column
      density="studio"
      top={<TopTitle studio>Produkty</TopTitle>}
      bottom={
        <>
          {note ? (
            <span role="alert" className="min-w-0 flex-1 text-class-gloss font-semibold text-class-wrong">
              ✗ {note}
            </span>
          ) : (
            <span className="min-w-0 flex-1 text-class-gloss text-class-ink-soft">Zmiany są widoczne na stronie od razu.</span>
          )}
          <div className="flex shrink-0 items-center gap-5">
            <button type="button" onClick={back} className="cl-link-quiet text-class-gloss">
              Wróć do listy
            </button>
            <Button type="submit" form="product-form" disabled={!dirty || save.isPending} className="min-h-11 text-class-gloss">
              {save.isPending ? 'Zapisuję…' : original ? 'Zapisz zmiany' : 'Dodaj produkt'}
            </Button>
          </div>
        </>
      }
    >
      <Main density="studio" width={820}>
        <div className="flex flex-col gap-7">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <h1 className="cl-heading m-0">{original ? original.title : 'Nowy produkt'}</h1>
            {original ? (
              <Status tone={original.active ? 'ok' : 'neutral'}>{original.active ? 'Widoczny na stronie' : 'Ukryty'}</Status>
            ) : (
              <span className="text-class-label font-semibold text-class-ink-faint">{LOCALE_LABELS[locale]}</span>
            )}
          </div>

          <form id="product-form" onSubmit={submit} className="grid grid-cols-1 gap-x-6 gap-y-5 desk:grid-cols-2">
            <Field id={id('kind')} label="Rodzaj" className="desk:col-span-2">
              <select id={id('kind')} value={d.kind} onChange={(e) => set('kind', e.target.value as ProductKind)} className="cl-input">
                {PRODUCT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_OPTION[k]}
                  </option>
                ))}
              </select>
            </Field>

            <Field id={id('title')} label="Nazwa">
              <input
                id={id('title')}
                required
                maxLength={120}
                value={d.title}
                onChange={(e) => {
                  const title = e.target.value
                  setNote(null)
                  setD((prev) => ({ ...prev, title, slug: prev.slugTouched ? prev.slug : slugify(title) }))
                }}
                className="cl-input"
              />
            </Field>
            <Field id={id('subtitle')} label="Podtytuł">
              <input id={id('subtitle')} maxLength={200} value={d.subtitle} onChange={(e) => set('subtitle', e.target.value)} className="cl-input" />
            </Field>

            <Field id={id('level')} label="Poziom">
              <input id={id('level')} maxLength={40} value={d.level} onChange={(e) => set('level', e.target.value)} placeholder="np. A0 → A1, B1–C2" className="cl-input" />
            </Field>
            <Field id={id('duration')} label="Czas trwania">
              <input id={id('duration')} maxLength={80} value={d.duration} onChange={(e) => set('duration', e.target.value)} placeholder="np. 3 miesiące" className="cl-input" />
            </Field>

            {!isFree && (
              <Field id={id('zloty')} label="Cena (zł)" hint={d.kind === 'bundle' ? 'Cena całego pakietu.' : undefined}>
                <input id={id('zloty')} inputMode="decimal" value={d.zloty} onChange={(e) => set('zloty', e.target.value)} className="cl-input" />
              </Field>
            )}
            {d.kind === 'bundle' && (
              <Field id={id('compare')} label="Cena regularna (zł)" hint="Suma kursów osobno. Karta pokazuje ją przekreśloną i liczy oszczędność.">
                <input id={id('compare')} inputMode="decimal" value={d.compareAtZloty} onChange={(e) => set('compareAtZloty', e.target.value)} className="cl-input" />
              </Field>
            )}

            <Field id={id('description')} label="Opis" className="desk:col-span-2">
              <textarea id={id('description')} rows={3} value={d.description} onChange={(e) => set('description', e.target.value)} className="cl-input resize-y" />
            </Field>

            <Field id={id('includes')} label={`${isFree ? 'W środku' : 'W cenie'} · jedna rzecz w linii`} className="desk:col-span-2">
              <textarea
                id={id('includes')}
                rows={Math.max(3, d.includes.split('\n').length)}
                value={d.includes}
                onChange={(e) => set('includes', e.target.value)}
                className="cl-input resize-y"
              />
            </Field>

            <Field id={id('ctaLabel')} label="Tekst przycisku">
              <input
                id={id('ctaLabel')}
                maxLength={60}
                value={d.ctaLabel}
                onChange={(e) => set('ctaLabel', e.target.value)}
                placeholder={isFree ? 'Puste = domyślny, np. Odbierz za darmo' : 'Puste = domyślny, np. Kup kurs'}
                className="cl-input"
              />
            </Field>
            <Field id={id('ctaUrl')} label="Link przycisku" hint="Sklep, plik, formularz. Puste = Telegram z ustawień w Cenniku, a gdy go nie ma — strona kontaktu.">
              <input
                id={id('ctaUrl')}
                maxLength={500}
                value={d.ctaUrl}
                onChange={(e) => set('ctaUrl', e.target.value)}
                placeholder="https://… albo /kontakt"
                className="cl-input"
              />
            </Field>

            <Field id={id('badge')} label="Etykieta na karcie" hint="Widoczna, gdy produkt jest wyróżniony.">
              <input id={id('badge')} maxLength={40} value={d.badge} onChange={(e) => set('badge', e.target.value)} placeholder="np. Najlepsza cena" className="cl-input" />
            </Field>
            <div className="flex flex-col justify-center gap-2.5 desk:pt-6">
              <Check checked={d.featured} onChange={(v) => set('featured', v)}>
                Wyróżniony na stronie
              </Check>
              <Check checked={d.active} onChange={(v) => set('active', v)}>
                Widoczny
              </Check>
            </div>

            <div className="desk:col-span-2">
              <CoverLine
                coverUrl={d.coverUrl}
                alt={d.title}
                onChange={(media) => {
                  setNote(null)
                  setD((prev) => ({ ...prev, coverImageId: media?.id ?? null, coverUrl: media?.url ?? null }))
                }}
              />
            </div>

            <More defaultOpen={false} summary={original ? 'Więcej ustawień: adres, język' : 'Więcej ustawień: adres'}>
              <Field id={id('slug')} label="Adres (slug)">
                <input
                  id={id('slug')}
                  maxLength={120}
                  value={d.slug}
                  onChange={(e) => {
                    setNote(null)
                    setD((prev) => ({ ...prev, slug: e.target.value, slugTouched: true }))
                  }}
                  className="cl-input"
                />
              </Field>
              {original && (
                <Field id={id('locale')} label="Język strony" hint="Zmiana przenosi produkt na stronę w tym języku.">
                  <select id={id('locale')} value={d.locale} onChange={(e) => set('locale', e.target.value as Locale)} className="cl-input">
                    {LOCALES.map((l) => (
                      <option key={l} value={l}>
                        {LOCALE_LABELS[l]}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </More>

            {original && (
              <p className="m-0 desk:col-span-2">
                <button
                  type="button"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Usunąć „${original.title}”? Tego nie da się cofnąć.`)) remove.mutate()
                  }}
                  className="cl-link-quiet text-class-gloss !text-class-wrong hover:!text-class-ink"
                >
                  Usuń produkt
                </button>
              </p>
            )}
          </form>
        </div>
      </Main>
    </Column>
  )
}

/* ── the form's data ───────────────────────────────────────────────────── */

type Draft = {
  locale: Locale
  slug: string
  slugTouched: boolean
  kind: ProductKind
  title: string
  subtitle: string
  description: string
  includes: string
  level: string
  duration: string
  zloty: string
  compareAtZloty: string
  badge: string
  ctaLabel: string
  ctaUrl: string
  coverImageId: string | null
  coverUrl: string | null
  active: boolean
  featured: boolean
  order: number
}

function emptyDraft(locale: Locale, order: number): Draft {
  return {
    locale,
    slug: '',
    slugTouched: false,
    kind: 'course',
    title: '',
    subtitle: '',
    description: '',
    includes: '',
    level: '',
    duration: '',
    zloty: '0',
    compareAtZloty: '0',
    badge: '',
    ctaLabel: '',
    ctaUrl: '',
    coverImageId: null,
    coverUrl: null,
    active: true,
    featured: false,
    order,
  }
}

function draftFromProduct(p: Product): Draft {
  return {
    locale: p.locale,
    slug: p.slug,
    slugTouched: true,
    kind: p.kind,
    title: p.title,
    subtitle: p.subtitle,
    description: p.description,
    includes: p.includes.join('\n'),
    level: p.level,
    duration: p.duration,
    zloty: String(groszToZloty(p.priceGrosz)),
    compareAtZloty: String(groszToZloty(p.compareAtGrosz)),
    badge: p.badge,
    ctaLabel: p.ctaLabel,
    ctaUrl: p.ctaUrl,
    coverImageId: p.coverImageId,
    coverUrl: p.coverUrl,
    active: p.active,
    featured: p.featured,
    order: p.order,
  }
}

const FIELD_NAMES: Record<string, string> = {
  locale: 'Język',
  slug: 'Adres (slug)',
  kind: 'Rodzaj',
  title: 'Nazwa',
  subtitle: 'Podtytuł',
  description: 'Opis',
  includes: 'W cenie',
  level: 'Poziom',
  duration: 'Czas trwania',
  priceGrosz: 'Cena',
  compareAtGrosz: 'Cena regularna',
  badge: 'Etykieta',
  ctaLabel: 'Tekst przycisku',
  ctaUrl: 'Link przycisku',
  coverImageId: 'Zdjęcie',
}

/** An emptied field is not 0 (Number('') is): a cleared price must be retyped, not saved as free. */
function money(raw: string): number | null {
  if (raw.trim() === '') return null
  const n = Number(raw.replace(',', '.'))
  return Number.isFinite(n) && n >= 0 ? zlotyToGrosz(n) : null
}

function draftToInput(d: Draft): { input: ProductInput } | { error: string } {
  const priceGrosz = d.kind === 'free' ? 0 : money(d.zloty)
  if (priceGrosz === null) return { error: 'Cena musi być liczbą, np. 400 albo 399,90.' }
  const compareAtGrosz = d.kind === 'bundle' ? money(d.compareAtZloty) : 0
  if (compareAtGrosz === null) return { error: 'Cena regularna musi być liczbą.' }

  const candidate = {
    locale: d.locale,
    slug: d.slug.trim() || slugify(d.title),
    kind: d.kind,
    title: d.title.trim(),
    subtitle: d.subtitle.trim(),
    description: d.description.trim(),
    includes: d.includes
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean),
    level: d.level.trim(),
    duration: d.duration.trim(),
    priceGrosz,
    compareAtGrosz,
    badge: d.badge.trim(),
    ctaLabel: d.ctaLabel.trim(),
    ctaUrl: d.ctaUrl.trim(),
    coverImageId: d.coverImageId,
    order: d.order,
    active: d.active,
    featured: d.featured,
  }

  const parsed = productInputSchema.safeParse(candidate)
  if (!parsed.success) {
    const issue = parsed.error.issues[0]
    const field = FIELD_NAMES[String(issue?.path[0] ?? '')] ?? 'Pole'
    return { error: `${field}: ${issue?.message ?? 'nieprawidłowa wartość'}` }
  }
  return { input: parsed.data }
}

function diffProduct(original: Product, input: ProductInput): ProductPatch {
  const patch: Record<string, unknown> = {}
  for (const key of Object.keys(input) as (keyof ProductInput)[]) {
    const before = original[key]
    const after = input[key]
    const same = Array.isArray(after) ? JSON.stringify(after) === JSON.stringify(before) : after === before
    if (!same) patch[key] = after
  }
  return patch as ProductPatch
}
